/**
 * A search only a person can run (WS-22) — a task, not a verdict. It says
 * where to go: each source with the link research tried for it (`o` opens
 * the first, in a new tab), or how a person reaches it offline; the names to
 * search for; then what came back: Nothing found (1) or Found something (2,
 * then what — ⌘↵ records), the date, the limits. Enter records. Or what
 * came back is a file (a docket, an extract), attached — with the search
 * filled in or not. Or the search isn't needed, and why.
 */

import { useEffect, useRef, useState } from 'react';

import {
  Anchor,
  Badge,
  Button,
  Group,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconExternalLink, IconPlugConnectedX } from '@tabler/icons-react';

import { sourcesOf, type ChecklistItem, type Intervention } from '@/types/workbench';
import { useWorkbenchKeys } from '../useWorkbenchKeys';
import { AttachFile, type AttachedFile } from './AttachFile';
import { AskHeading, Kbd, listOf, useAnswer } from './common';
import { NotNeeded } from './NotNeeded';
import classes from '../NeedsYou.module.css';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function split(text: string): string[] {
  return text
    .split(/[;\n]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

interface SearchPaneProps {
  reportId: string;
  ask: Intervention;
  subjectName: string | null;
  /** The checklist item the search is for. */
  item: ChecklistItem | null;
  onAnswered: (ids: string[]) => void | Promise<void>;
  keyboard: boolean;
}

export function SearchPane({
  reportId,
  ask,
  subjectName,
  item,
  onAnswered,
  keyboard,
}: Readonly<SearchPaneProps>) {
  const { submitting, submit } = useAnswer(reportId, onAnswered);
  const sources = sourcesOf(ask.payload);
  const asked = listOf(ask.payload.names);
  const [names, setNames] = useState(asked.join('; '));
  const [result, setResult] = useState<'none_found' | 'found' | ''>('');
  const [details, setDetails] = useState('');
  const [searchedOn, setSearchedOn] = useState(today);
  const [limits, setLimits] = useState('');
  const [attached, setAttached] = useState<AttachedFile | null>(null);
  const detailsBox = useRef<HTMLTextAreaElement | null>(null);
  const online = sources.filter((s) => s.url);
  const searched =
    sources.length > 0 &&
    split(names).length > 0 &&
    result !== '' &&
    (result === 'none_found' || details.trim().length > 0);
  const ready = searched || attached !== null;

  // "Found something" asks what: the box takes the focus.
  useEffect(() => {
    if (result === 'found') {
      detailsBox.current?.focus();
    }
  }, [result]);

  const record = () => {
    if (!ready || submitting) {
      return;
    }
    void submit(ask, {
      ...(searched
        ? {
            search: {
              sources: sources.map((s) => s.name),
              names: split(names),
              result,
              details: details.trim() || null,
              searched_on: searchedOn || null,
              limits: limits.trim() || null,
            },
          }
        : {}),
      ...(attached ? { conversation_attachment_id: attached.id } : {}),
    });
  };

  const openFirst = () => {
    if (online[0]?.url) {
      window.open(online[0].url, '_blank', 'noopener,noreferrer');
    }
  };

  useWorkbenchKeys(
    {
      o: openFirst,
      '1': () => setResult('none_found'),
      '2': () => setResult('found'),
      Enter: record,
      'Mod+Enter': record,
    },
    keyboard
  );

  return (
    <>
      <div className={classes.detailBody} data-testid="pane-search">
        <Stack gap="sm">
          <AskHeading
            section="searches"
            subjectName={subjectName}
            question={ask.question}
            why={ask.reason}
            blocking={ask.waitingTaskId !== null}
          />
          {item && (
            <Text size="xs" c="dimmed">
              For the check: {item.key} {item.title}
              {item.note ? ` — ${item.note}` : ''}
            </Text>
          )}
          <Stack gap={4} data-testid="search-sources">
            {sources.map((source) => (
              <Group key={source.name} gap={6} wrap="nowrap" align="flex-start">
                {source.url ? (
                  <>
                    <IconExternalLink size={14} style={{ flex: 'none', marginTop: 3 }} />
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      <Anchor
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        data-testid="search-source-link"
                      >
                        {source.name}
                      </Anchor>
                      <Text size="xs" c="dimmed" truncate>
                        {source.url.replace(/^https?:\/\//, '')}
                      </Text>
                    </Stack>
                  </>
                ) : (
                  <>
                    <IconPlugConnectedX
                      size={14}
                      color="var(--mantine-color-orange-6)"
                      style={{ flex: 'none', marginTop: 3 }}
                    />
                    <Stack gap={0}>
                      <Group gap={6}>
                        <Text size="sm">{source.name}</Text>
                        {source.access && (
                          <Badge
                            size="xs"
                            color="orange"
                            variant="light"
                            style={{ textTransform: 'none' }}
                          >
                            offline
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {source.access ?? 'No link: search it where you usually do.'}
                      </Text>
                    </Stack>
                  </>
                )}
              </Group>
            ))}
          </Stack>
          {asked.length > 0 ? (
            <Text size="xs" c="dimmed" data-testid="search-names">
              For: {asked.join('; ')}
              {online.length > 0 && (
                <>
                  {' '}
                  · <Kbd>o</Kbd> opens the first source
                </>
              )}
            </Text>
          ) : (
            <TextInput
              size="xs"
              label="For"
              description="The names you searched, separated by semicolons"
              value={names}
              onChange={(e) => setNames(e.currentTarget.value)}
              data-testid="search-names-input"
            />
          )}
        </Stack>
      </div>
      <Stack gap={6} className={classes.answers}>
        <Group gap={6} wrap="wrap" align="flex-end">
          <SegmentedControl
            size="xs"
            value={result}
            onChange={(v) => setResult(v as 'none_found' | 'found')}
            data={[
              { label: 'Nothing found (1)', value: 'none_found' },
              { label: 'Found something (2)', value: 'found' },
            ]}
            data-testid="search-result"
          />
          <TextInput
            size="xs"
            type="date"
            aria-label="Searched on"
            value={searchedOn}
            onChange={(e) => setSearchedOn(e.currentTarget.value)}
            w={150}
          />
          <TextInput
            size="xs"
            placeholder="Limits, e.g. trading names only"
            aria-label="Limits"
            value={limits}
            onChange={(e) => setLimits(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 180 }}
          />
        </Group>
        {result === 'found' && (
          <Textarea
            size="xs"
            autosize
            minRows={2}
            placeholder="What was found (⌘↵ records)"
            aria-label="What was found"
            ref={detailsBox}
            value={details}
            onChange={(e) => setDetails(e.currentTarget.value)}
            data-testid="search-details"
          />
        )}
        <Group gap={6} justify="flex-end" wrap="nowrap">
          <AttachFile reportId={reportId} attached={attached} onChange={setAttached} />
          <Button
            size="xs"
            onClick={record}
            disabled={!ready}
            loading={submitting}
            data-testid="search-record"
          >
            Record the search
            <Kbd>Enter</Kbd>
          </Button>
        </Group>
        <NotNeeded onDecline={(why) => submit(ask, { not_needed: true, note: why })} />
      </Stack>
    </>
  );
}
